"use client";

import { useRouter } from "next/router";
import { createContext, useEffect, useMemo, useRef, useState } from "react"

export const LayoutContext = createContext(null);

function LayoutContextContainer({children}) {
  /* Use for refreshing the cookie police component state, and other layiout elements */
  const [cookieStored, setCookieStored] = useState(false);
  const [showCookieConsent, setShowCookieConsent] = useState(null);
  /* Show/Hides mobile menu state */
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  /* interstitial */
  const [interstitialStateOpen, setInterstitialStateOpen] = useState(false);
  const [interstitialLeaveSite, setInterstitialLeaveSite] = useState(false);
  const [interstitialHref, setInterstitialHref] = useState('');
  const [interstitialActionLabel, setInterstitialActionLabel] = useState();
  /* sticky header state */
  const [stickyHeaderFixedState, setStickyHeaderFixedState] = useState(false);
  /* screenshot mode */
  const [screenshotMode, setScreenshotMode] = useState(false);
  /* define page mode */
  // const [pageMode, setPageMode] = useState(''); // default for Patient
  /* Standalone HCP site since the crenessity.com/hcp -> crenessityhcp.com split */
  const [isHcpRoute, setIsHcpRoute] = useState(true);
  /* Router ready */
  const [routerIsReady, setRouterIsReady] = useState(false);
  /* Toolip */
  const tooltipRef = useRef(null);
  /* isi height */
  const [isiMobileStateCollapsed, setIsiMobileStateCollapsed] = useState(false);
  const [isiHeight, _setIsiHeight] = useState(84); // min default val
  const isiHeightRef = useRef(isiHeight);
  function setIsiHeight(val) {
    isiHeightRef.current = val;
    _setIsiHeight(val);
  }
  /* is mobile */
  const [isMobile, setIsMobile] = useState(false);
  /* indications state */
  const [indicationsMobileStateCollapsed, setIndicationsMobileStateCollapsed] = useState(false);
  /* Captcha Key by host env */
  const captchKey = useMemo(() => {
    return process.env.NEXT_PUBLIC_HOSTING_ENVIRONMENT === "PRODUCTION"
      ? process.env.NEXT_PUBLIC_RECAPTCHA_KEY_PRODUCTION
      : process.env.NEXT_PUBLIC_RECAPTCHA_KEY_STAGING;
  }, []);
  /* Auto Scroll */
  const [autoScrollActive, setAutoScrollActive] = useState(false);
  /* Footer override end matter (e.g., based on page state) */
  const [footerOverrideEndMatter, setFooterOverrideEndMatter] = useState(null);

  const router = useRouter();

  /* useEffect(() => {
    // this handles the page mode (default and hcp)
    const handleRouteChange = (url) => {
      setPageMode(url?.startsWith('/hcp') ? 'hcp' : '');
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    // Cleanup the event listener
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router]);
 */


  useEffect(() => {
    function handleResize() {
      if (window.innerWidth >= 700) {
        // flag for mobile view
        setIsMobile(false);
      }else {
        setIsMobile(true);
      }
    }
    handleResize();

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    //router.isReady && setPageMode(router.asPath?.startsWith('/hcp') ? 'hcp' : '');
    routerIsReady && router.query['screenshot-mode'] === 'true' && setScreenshotMode(true);
  }, [router.query, routerIsReady]);

  useEffect(() => {
    // reset state
    if (cookieStored) {
      setTimeout(() => {
        setCookieStored(false);
      }, 333);
    }
  }, [cookieStored]);

  useEffect(() => setRouterIsReady(router.isReady), [router.isReady]);

  useEffect(() => {
    if (autoScrollActive) {
      setTimeout(() => {
        setAutoScrollActive(false);
      }, 1000);
    }
  }, [autoScrollActive]);

  const provider = useMemo(() => {
    function getCookieName() {
      return isHcpRoute ? 'NeurocrineCREN_HCP' : 'NeurocrineCREN_DTC';
    }

    function setCookieConsent() {
      const name = `${getCookieName()}=Consent;`;
      const expDays = 30;
      const date = new Date();
      const securePart = window.location.protocol === 'https:' ? '; Secure' : '';

      date.setTime(date.getTime() + (expDays * 24 * 60 * 60 * 1000));
      const expires = `expires=${date.toUTCString()};`;

      // Keep parity with environments where HTTP is used (local/staging).
      document.cookie = `${name}${expires}path=/; SameSite=Lax${securePart}`;

      setCookieStored(true);
    }

    function cookieConsent() {
      let name = `${getCookieName()}=`;
      let decodedCookie = decodeURIComponent(document.cookie);
      let ca = decodedCookie.split(';');

      for(let i = 0; i <ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') {
          c = c.substring(1);
        }
        if (c.indexOf(name) === 0) {
          return c.substring(name.length, c.length);
        }
      }
      return "";
    }

    return {
      cookieConsent,
      setCookieConsent,
      cookieStored,
      setCookieStored,
      showCookieConsent,
      setShowCookieConsent,
      showMobileMenu,
      setShowMobileMenu,
      interstitialStateOpen,
      setInterstitialStateOpen,
      interstitialLeaveSite,
      setInterstitialLeaveSite,
      interstitialHref,
      setInterstitialHref,
      interstitialActionLabel,
      setInterstitialActionLabel,
      stickyHeaderFixedState,
      setStickyHeaderFixedState,
      screenshotMode,
      setScreenshotMode,
      isHcpRoute,
      setIsHcpRoute,
      routerIsReady,
      setRouterIsReady,
      tooltipRef,
      isiHeight,
      setIsiHeight,
      isiMobileStateCollapsed,
      setIsiMobileStateCollapsed,
      isiHeightRef,
      isMobile,
      indicationsMobileStateCollapsed,
      setIndicationsMobileStateCollapsed,
      captchKey,
      autoScrollActive,
      setAutoScrollActive,
      footerOverrideEndMatter,
      setFooterOverrideEndMatter,
      // pageMode,
      // setPageMode,
    }}, [
      cookieStored,
      showCookieConsent,
      showMobileMenu,
      interstitialStateOpen,
      interstitialLeaveSite,
      interstitialHref,
      interstitialActionLabel,
      stickyHeaderFixedState,
      screenshotMode,
      isHcpRoute,
      routerIsReady,
      tooltipRef,
      isiHeight,
      isiMobileStateCollapsed,
      isiHeightRef,
      isMobile,
      indicationsMobileStateCollapsed,
      captchKey,
      autoScrollActive,
      footerOverrideEndMatter,
      // pageMode,
    ]
  );

  return (
    <>
      <LayoutContext.Provider value={provider}>
        {children}
      </LayoutContext.Provider>
    </>
  )
}

export default LayoutContextContainer;